import { Check, X } from "lucide-react";
import {
  formatDeskDisplayDate,
  formatDeskDisplayDateTime,
} from "../../../../utils/deskActionMatrix";
import { leaveLinkedCancellationCaption } from "../../../../utils/leaveRequestDesk";
import { linkedCancellationTrailSteps } from "./LeaveLinkedCancellationBlock";

const trailDotClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "acknowledged") {
    return "border-[hsl(var(--ds-success))] bg-[hsl(var(--ds-success))] text-white";
  }
  if (s === "rejected") {
    return "border-[hsl(var(--ds-danger))] bg-[hsl(var(--ds-danger))] text-white";
  }
  if (s === "pending") {
    return "border-[hsl(var(--ds-warning))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-warning-fg))]";
  }
  return "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]";
};

function actorLabelForStep(step) {
  const statusLower = String(step?.status || "").toLowerCase();
  const actorFallback =
    statusLower === "pending" || statusLower === "current" ? "Next approver" : null;
  const rawActor = step?.displayText || step?.actor || actorFallback;
  if (rawActor && !/^approver$/i.test(String(rawActor).trim())) return rawActor;
  return actorFallback || "Unknown";
}

export function DeskSideTrail({ steps = [], emptyText = "" }) {
  if (!steps.length) {
    return emptyText ? (
      <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{emptyText}</p>
    ) : null;
  }
  return (
    <ol className="relative space-y-0">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const statusText = step?.status === "Undo" ? "Recall" : step?.status || "-";
        const statusLower = String(step?.status || "").toLowerCase();
        const actorLabel = actorLabelForStep(step);
        const note = step?.note || step?.reason || "";
        const isCancel = step?._source === "cancellation";
        return (
          <li
            key={`${step?.actedAt || idx}-${step?.status || "step"}-${step?._source || "leave"}`}
            className="relative flex gap-3 pb-4 last:pb-0"
          >
            {!isLast ? (
              <span
                className="absolute left-[11px] top-6 bottom-0 w-px bg-[hsl(var(--ds-border))]"
                aria-hidden
              />
            ) : null}
            <span
              className={`relative z-[1] mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${trailDotClass(
                step?.status
              )}`}
            >
              {statusLower === "approved" || statusLower === "acknowledged" ? (
                <Check size={12} strokeWidth={2.5} />
              ) : statusLower === "rejected" ? (
                <X size={12} strokeWidth={2.5} />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </span>
            <div className="min-w-0 pt-0.5">
              <p className="truncate text-xs font-semibold text-[hsl(var(--ds-fg))]">
                {actorLabel}
              </p>
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                {statusText}
                {step?.actedAt ? ` · ${formatDeskDisplayDateTime(step.actedAt)}` : ""}
              </p>
              {isCancel ? (
                <p className="mt-0.5 text-[11px] font-medium text-[hsl(var(--ds-danger))]">
                  Leave cancellation
                </p>
              ) : note && !String(note).toLowerCase().includes("queued for") ? (
                <p className="mt-0.5 line-clamp-2 text-[11px] text-[hsl(var(--ds-fg))]">
                  {note}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function LeaveFactsCard({ formData = {}, dangerBorder = false }) {
  const duration = String(formData.leaveDuration || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const from = formData.fromDate || "";
  const to = formData.toDate || "";
  const sameDay = from && to && String(from).slice(0, 10) === String(to).slice(0, 10);
  const dateLine = sameDay
    ? formatDeskDisplayDate(from)
    : [from, to]
        .filter(Boolean)
        .map((d) => formatDeskDisplayDate(d))
        .filter(Boolean)
        .join(" → ");
  const title = dangerBorder
    ? "Leave cancellation"
    : formData.leaveType || "Leave";
  const caption = dangerBorder
    ? leaveLinkedCancellationCaption({ status: formData._cancelStatus || "Approved" })
    : "";
  const approved = String(formData._cancelStatus || "").toLowerCase() === "approved";

  return (
    <div
      className={`rounded-xl border bg-[hsl(var(--ds-muted))]/30 p-3 ${
        dangerBorder
          ? "border-[hsl(var(--ds-danger))]"
          : "border-[hsl(var(--ds-border))]"
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-base font-semibold text-[hsl(var(--ds-fg))]">{title}</span>
        {dangerBorder ? (
          <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
            · {formData._cancelStatus || "Approved"}
          </span>
        ) : duration ? (
          <span className="text-xs text-[hsl(var(--ds-muted-fg))]">· {duration}</span>
        ) : null}
      </div>
      {caption ? (
        <p
          className={`mt-1 text-sm ${
            approved
              ? "font-medium text-[hsl(var(--ds-danger))]"
              : "text-[hsl(var(--ds-fg))]"
          }`}
        >
          {caption}
        </p>
      ) : dateLine ? (
        <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">{dateLine}</p>
      ) : null}
      {dangerBorder && dateLine ? (
        <p className="mt-1 text-sm text-[hsl(var(--ds-fg))]">{dateLine}</p>
      ) : null}
      {formData.reason ? (
        <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
          {formData.reason}
        </p>
      ) : null}
      {formData.notes ? (
        <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
          Note: {formData.notes}
        </p>
      ) : null}
    </div>
  );
}

function TrackingRow({ facts, trailSteps, emptyTrailText }) {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,17rem)]">
      <div className="min-w-0">{facts}</div>
      <div className="min-w-0">
        <DeskSideTrail steps={trailSteps} emptyText={emptyTrailText} />
      </div>
    </div>
  );
}

/** Standard desk detail: facts card on the left, approver trail on the right. */
export function DeskRequestTrackingLayout({
  facts,
  trailSteps = [],
  pendingHint = null,
}) {
  return (
    <div className="space-y-4">
      <TrackingRow facts={facts} trailSteps={trailSteps} />
      {pendingHint}
    </div>
  );
}

/** Original leave + nested cancellation, each with its approver on the same row. */
export default function LeaveDeskTrackingLayout({
  formData = {},
  leaveTrailSteps = [],
  linkedCancellation = null,
  pendingHint = null,
}) {
  const cancelSteps = linkedCancellationTrailSteps(linkedCancellation);
  return (
    <div className="space-y-4">
      <TrackingRow
        facts={<LeaveFactsCard formData={formData} />}
        trailSteps={leaveTrailSteps}
      />
      {linkedCancellation ? (
        <TrackingRow
          facts={
            <LeaveFactsCard
              dangerBorder
              formData={{
                leaveType: formData.leaveType,
                leaveDuration: formData.leaveDuration,
                fromDate: formData.fromDate,
                toDate: formData.toDate,
                reason: linkedCancellation.reason,
                notes: linkedCancellation.notes,
                _cancelStatus: linkedCancellation.status,
              }}
            />
          }
          trailSteps={cancelSteps}
        />
      ) : null}
      {pendingHint}
    </div>
  );
}
